import axios from 'axios'
import AuthService from './auth-header'
import {
  REIMBURSEMENT_STATUSES,
  normalizeReimbursementStatus,
  formatReimbursementStatusLabel,
  formatReimbursementStageOutcome,
  getReimbursementStageName,
  getRememberedReimbursementOnBehalf,
} from '@/utils/reimbursement-permissions'
import hrEmailNotificationService, {
  HR_EMAIL_TEMPLATE_KEYS,
  isMailHrSendSuccess,
} from '@/services/hr-email-notification-service'
import mailService from '@/services/mail-service'
import {
  REIMBURSEMENT_EMAIL_ENABLED,
  REIMBURSEMENT_EMAIL_CC,
  getReimbursementFilePath,
} from '@/utils/reimbursement-config'

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/'
} else {
  API_URL = 'http://192.168.0.30:5000/api/'
}

const SERVICE_UNAVAILABLE = {
  status: 'SERVICE_UNAVAILABLE',
  message: 'Reimbursement service unavailable. Please contact admin.',
  data: [],
}

function parseMulterErrorMessage(raw) {
  if (typeof raw !== 'string' || !raw.includes('MulterError')) return null
  if (raw.includes('Unexpected field')) {
    return (
      'The server rejected the attachment. POST /reimbursements does not accept file '
      + 'uploads yet (multer is configured with .none()). Submit without an attachment '
      + 'or ask the backend team to enable upload for expenseFile.'
    )
  }
  if (raw.includes('LIMIT_FILE_SIZE')) {
    return 'Attachment is too large. Supporting documents must be 1.5 MB or smaller.'
  }
  return 'File upload failed. Try again without an attachment.'
}

function apiError(err, fallbackMessage = SERVICE_UNAVAILABLE.message) {
  if (err?.response?.data) {
    const data = err.response.data
    if (typeof data === 'string') {
      const multerMessage = parseMulterErrorMessage(data)
      return {
        status: 'ERROR',
        message: multerMessage || data || fallbackMessage,
      }
    }
    return data
  }
  return { status: 'ERROR', message: fallbackMessage }
}

/** Let axios set multipart boundary; manual Content-Type breaks FormData parsing. */
function multipartAuthHeaders() {
  const headers = AuthService.authHeaderForm()
  delete headers['Content-Type']
  return headers
}

/** Extract reimbursement row from approve/reject/create API response. */
export function extractReimbursementRow(apiResponse) {
  if (!apiResponse || typeof apiResponse !== 'object') return null
  const row = apiResponse.data ?? apiResponse
  if (!row || typeof row !== 'object' || row.id == null) return null
  return row
}

export function formatReimbursementApiError(res, fallback = 'Request failed.') {
  if (!res) return fallback
  if (typeof res === 'string') {
    return parseMulterErrorMessage(res) || res || fallback
  }
  const multerMessage = parseMulterErrorMessage(res.message)
  if (multerMessage) return multerMessage
  const base = res.message || fallback
  if (Array.isArray(res.errors) && res.errors.length > 0) {
    return `${base} ${res.errors.join(' ')}`
  }
  return base
}

function buildFilterQuery(filters = {}) {
  const {
    account = '',
    business_unit = '',
    category = '',
    sub_category = '',
    dateFilterSelected = '',
    start_date = '',
    end_date = '',
    gst_tds = '',
    location = '',
    payment_mode = '',
  } = filters
  return (
    '?account=' + encodeURIComponent(account)
    + '&business_unit=' + encodeURIComponent(business_unit)
    + '&category=' + encodeURIComponent(category)
    + '&sub_category=' + encodeURIComponent(sub_category)
    + '&dateFilterSelected=' + encodeURIComponent(dateFilterSelected)
    + '&start_date=' + encodeURIComponent(start_date)
    + '&end_date=' + encodeURIComponent(end_date)
    + '&gst_tds=' + encodeURIComponent(gst_tds)
    + '&location=' + encodeURIComponent(location)
    + '&payment_mode=' + encodeURIComponent(payment_mode)
  )
}

function parseAddedBy(addedBy) {
  if (!addedBy) return {}
  if (typeof addedBy === 'string') {
    try { return JSON.parse(addedBy) } catch (e) { return {} }
  }
  return addedBy
}

function formatApproverName(snapshot) {
  if (!snapshot) return '-'
  if (typeof snapshot === 'string') {
    try {
      const p = JSON.parse(snapshot)
      return p.title || p.name || '-'
    } catch (e) {
      return snapshot
    }
  }
  return snapshot.title || snapshot.name || '-'
}

function getLocalUserEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    return userData.emp_id || null
  } catch (e) {
    return null
  }
}

/** Unwrap API envelopes and merge optional submit-time fallback (create often returns a sparse row). */
function normalizeReimbursementEmailRow(row, fallback = {}) {
  let source = row?.data && typeof row.data === 'object' && !Array.isArray(row.data)
    ? { ...row, ...row.data }
    : (row || {})
  if (fallback?.data && typeof fallback.data === 'object' && !Array.isArray(fallback.data)) {
    source = { ...source, ...fallback.data }
  }
  return { ...fallback, ...source }
}

function formatReimbursementAmount(amount) {
  if (amount == null || amount === '') return '-'
  const num = Number(amount)
  if (Number.isNaN(num)) return String(amount)
  return num.toFixed(2)
}

function buildReimbursementEmailVariables(row, extra = {}) {
  const fallback = extra.fallback || extra.form || {}
  row = normalizeReimbursementEmailRow(row, fallback)
  const submitter = parseAddedBy(row.added_by || row.addedBy || fallback.added_by || fallback.addedBy)
  const nestedSubmitter = parseAddedBy(submitter.submitted_by || submitter.raised_by || row.submitted_by || row.raised_by || fallback.submitted_by)
  const actor = (nestedSubmitter.title || nestedSubmitter.name)
    ? nestedSubmitter
    : submitter
  const employeeName = row.employee_name
    || row.employeeName
    || submitter.employee_name
    || submitter.on_behalf_of
    || fallback.employee_name
    || actor.title
    || actor.name
    || 'Employee'
  const employeeId = row.employee_id
    || row.employeeId
    || submitter.employee_id
    || submitter.applied_for
    || fallback.employee_id
    || actor.empId
    || actor.emp_id
    || fallback.empId
    || fallback.emp_id
    || getLocalUserEmpId()
    || '-'
  const claimId = row.id ?? row.claim_id ?? fallback.id ?? fallback.claimId
  const item = row.item
    || fallback.item
    || fallback.itemName
    || row.comment
    || fallback.comment
  return {
    EmployeeName: employeeName || 'Employee',
    EmployeeId: employeeId,
    SubmittedBy: actor.title || actor.name || employeeName || 'Employee',
    ManagerName: extra.ManagerName || 'Approver',
    Amount: formatReimbursementAmount(row.amount ?? fallback.amount),
    Category: row.category || fallback.category || 'Indirect',
    SubCategory: row.sub_category || row.subCategory || fallback.sub_category || fallback.subCategory || '-',
    ExpenseDate: row.expense_date || row.expenseDate || fallback.expense_date || fallback.expenseDate || '-',
    Account: row.account || fallback.account || fallback.accountName || '-',
    Vertical: row.vertical || fallback.vertical || '-',
    Status: formatReimbursementStatusLabel(extra.Status || row),
    ItemDescription: item || '-',
    ClaimId: claimId != null && claimId !== '' ? String(claimId) : '-',
    PaymentMode: row.payment_mode || row.paymentMode || fallback.payment_mode || fallback.paymentMode || '-',
    Location: row.location || fallback.location || '-',
    Comment: row.comment || fallback.comment || '-',
    ApprovedBy: extra.ApprovedBy || '-',
    RejectedBy: extra.RejectedBy || '-',
    RejectionComment: extra.RejectionComment || row.rejection_comment || row.rejectionComment || '-',
    Stage: extra.Stage
      ? (getReimbursementStageName(extra.Stage) || extra.Stage)
      : (row.rejected_stage || row.rejectedStage
        ? formatReimbursementStageOutcome(row.rejected_stage || row.rejectedStage, 'rejected')
        : '-'),
    Remarks: extra.Remarks || '-',
    ApprovalStage: extra.ApprovalStage
      ? (getReimbursementStageName(extra.ApprovalStage) || extra.ApprovalStage)
      : '-',
    StageApproved: extra.StageApproved
      || formatReimbursementStageOutcome(
        extra.previousStatus || extra.ApprovalStage || extra.Stage || REIMBURSEMENT_STATUSES.PENDING_L1,
        'approved',
      ),
  }
}

function getSubmitterEmail(row) {
  const fromRow = parseAddedBy(row?.added_by).email
  if (fromRow) return fromRow
  try {
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    return userData.email || null
  } catch (e) {
    return null
  }
}

/**
 * mail-hr attaches files via getFileStream(S3 key) on `filename`.
 * Skip URLs and legacy local `uploads/` paths — those 500 the mail API.
 */
function resolveReimbursementEmailAttachmentKey(row) {
  const filePath = getReimbursementFilePath(row)
  if (!filePath) return ''
  const path = String(filePath).trim().replace(/^\//, '')
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return ''
  if (/^(public\/)?uploads\//i.test(path) || path.startsWith('./')) return ''
  return path
}

async function ensureReimbursementAttachmentKey(row) {
  const fromRow = resolveReimbursementEmailAttachmentKey(row)
  if (fromRow) return fromRow
  const id = row?.id ?? row?.claim_id
  if (id == null || id === '') return ''
  try {
    const res = await axios.get(`${API_URL}reimbursements/${id}`, {
      headers: AuthService.authHeader(),
    })
    const fetched = extractReimbursementRow(res.data) || res.data?.data || res.data
    return resolveReimbursementEmailAttachmentKey(fetched)
  } catch (err) {
    console.warn('[reimbursement-email] could not resolve attachment key:', err?.message || err)
    return ''
  }
}

function buildOnBehalfSubmittedHtml(variables = {}) {
  const claimId = variables.ClaimId || '-'
  const employeeName = variables.EmployeeName || 'Employee'
  const submittedBy = variables.SubmittedBy || employeeName
  return (
    '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px;">'
    + '<div style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.08);">'
    + '<div style="background-color: #00AC99; color: #fff; padding: 16px 24px;">'
    + '<h2 style="margin: 0; font-size: 16px;">Sattva Human — Reimbursements</h2>'
    + '<p style="margin: 4px 0 0 0; font-size: 13px; opacity: 0.9;">Reimbursement Submitted</p>'
    + '</div>'
    + '<div style="padding: 24px;">'
    + `<p>Hello ${employeeName},</p>`
    + `<p>A reimbursement claim was submitted on your behalf by <strong>${submittedBy}</strong> and is awaiting Level 1 approval.</p>`
    + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:160px;">Claim ID</td><td>#${claimId}</td></tr>`
    + `<tr><td style="font-weight:bold;">Employee</td><td>${employeeName}</td></tr>`
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Employee ID</td><td>${variables.EmployeeId || '-'}</td></tr>`
    + `<tr><td style="font-weight:bold;">Item / Description</td><td>${variables.ItemDescription || '-'}</td></tr>`
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Category</td><td>${variables.Category || '-'}</td></tr>`
    + `<tr><td style="font-weight:bold;">Amount</td><td>${variables.Amount || '-'}</td></tr>`
    + '</table>'
    + '<p>The supporting document is attached to this email.</p>'
    + '</div>'
    + '<div style="text-align: center; padding: 16px 24px; border-top: 1px solid #eee; color: #888; font-size: 12px;">'
    + '<p style="margin: 0;">This is an automated notification — please do not reply to this email.</p>'
    + '</div>'
    + '</div>'
    + '</div>'
  )
}

function buildWithdrawnHtml(variables = {}) {
  const claimId = variables.ClaimId || '-'
  const employeeName = variables.EmployeeName || 'Employee'
  const submittedBy = variables.SubmittedBy || employeeName
  return (
    '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px;">'
    + '<div style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.08);">'
    + '<div style="background-color: #00AC99; color: #fff; padding: 16px 24px;">'
    + '<h2 style="margin: 0; font-size: 16px;">Sattva Human — Reimbursements</h2>'
    + '<p style="margin: 4px 0 0 0; font-size: 13px; opacity: 0.9;">Claim Withdrawn</p>'
    + '</div>'
    + '<div style="padding: 24px;">'
    + `<p>Hello ${employeeName},</p>`
    + `<p>Reimbursement claim <strong>#${claimId}</strong> has been <strong>withdrawn</strong> by ${submittedBy}.</p>`
    + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:160px;">Claim ID</td><td>#${claimId}</td></tr>`
    + `<tr><td style="font-weight:bold;">Employee</td><td>${employeeName}</td></tr>`
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Item / Description</td><td>${variables.ItemDescription || '-'}</td></tr>`
    + `<tr><td style="font-weight:bold;">Amount</td><td>${variables.Amount || '-'}</td></tr>`
    + '</table>'
    + '</div>'
    + '<div style="text-align: center; padding: 16px 24px; border-top: 1px solid #eee; color: #888; font-size: 12px;">'
    + '<p style="margin: 0;">This is an automated notification — please do not reply to this email.</p>'
    + '</div>'
    + '</div>'
    + '</div>'
  )
}

function buildSupportingDocumentHtml(variables = {}) {
  const claimId = variables.ClaimId || '-'
  const employeeName = variables.EmployeeName || 'Employee'
  const item = variables.ItemDescription || '-'
  const amount = variables.Amount || '-'
  return (
    '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px;">'
    + '<div style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.08);">'
    + '<div style="background-color: #00AC99; color: #fff; padding: 16px 24px;">'
    + '<h2 style="margin: 0; font-size: 16px;">Sattva Human — Reimbursements</h2>'
    + '<p style="margin: 4px 0 0 0; font-size: 13px; opacity: 0.9;">Supporting Document</p>'
    + '</div>'
    + '<div style="padding: 24px;">'
    + `<p>Hi ${employeeName},</p>`
    + `<p>The supporting document for reimbursement claim <strong>#${claimId}</strong> is attached to this email.</p>`
    + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:160px;">Claim ID</td><td>${claimId}</td></tr>`
    + `<tr><td style="font-weight:bold;">Item / Description</td><td>${item}</td></tr>`
    + `<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Amount</td><td>${amount}</td></tr>`
    + '</table>'
    + '<p>Please keep this receipt with the claim for approval and records.</p>'
    + '</div>'
    + '<div style="text-align: center; padding: 16px 24px; border-top: 1px solid #eee; color: #888; font-size: 12px;">'
    + '<p style="margin: 0;">This is an automated notification — please do not reply to this email.</p>'
    + '</div>'
    + '</div>'
    + '</div>'
  )
}

async function sendReimbursementEmailSafe(payload) {
  const result = await hrEmailNotificationService.sendTemplatedEmail(payload)
  if (result?.status === 'OK') {
    return result
  }

  const failure = {
    template_key: payload.template_key,
    to: payload.to,
    status: result?.status || 'FAILED',
    detail: result?.detail,
  }
  console.warn('[reimbursement-email] send failed (non-blocking):', failure)
  const err = new Error('reimbursement email failed')
  err.emailFailure = failure
  throw err
}

async function sendOnBehalfSubmittedEmailSafe({ to, cc, variables = {}, filename }) {
  if (!to) {
    return { status: 'SKIPPED' }
  }
  const claimId = variables.ClaimId || '-'
  const mailData = {
    to,
    cc: cc || '',
    from: 'connect@sattvahuman.com',
    subject: `Reimbursement Submitted — #${claimId}`,
    html: buildOnBehalfSubmittedHtml(variables),
    filename: filename || '',
  }
  const result = await mailService.mailHR(mailData)
  if (isMailHrSendSuccess(result)) {
    return result
  }
  const failure = {
    template_key: 'reimbursement_submitted_on_behalf',
    to: 'beneficiary',
    status: result?.status || 'FAILED',
    detail: result,
  }
  console.warn('[reimbursement-email] on-behalf submit send failed (non-blocking):', failure)
  const err = new Error('reimbursement email failed')
  err.emailFailure = failure
  throw err
}

async function sendWithdrawnEmailSafe({ to, cc, variables = {}, filename }) {
  if (!to) {
    return { status: 'SKIPPED' }
  }
  const claimId = variables.ClaimId || '-'
  const mailData = {
    to,
    cc: cc || '',
    from: 'connect@sattvahuman.com',
    subject: `Reimbursement Withdrawn — #${claimId}`,
    html: buildWithdrawnHtml(variables),
    filename: filename || '',
  }
  const result = await mailService.mailHR(mailData)
  if (isMailHrSendSuccess(result)) {
    return result
  }
  const failure = {
    template_key: 'reimbursement_withdrawn',
    to: 'employee',
    status: result?.status || 'FAILED',
    detail: result,
  }
  console.warn('[reimbursement-email] withdraw send failed (non-blocking):', failure)
  const err = new Error('reimbursement email failed')
  err.emailFailure = failure
  throw err
}

/** POST /api/mail-hr with `filename` = S3 key (same contract as offer-letter). */
async function sendReimbursementAttachmentEmailSafe({ to, variables = {}, filename }) {
  if (!to || !filename) {
    return { status: 'SKIPPED' }
  }
  const claimId = variables.ClaimId || '-'
  const mailData = {
    to,
    cc: REIMBURSEMENT_EMAIL_CC || '',
    subject: `Reimbursement #${claimId} — Supporting Document`,
    html: buildSupportingDocumentHtml(variables),
    filename,
  }
  const result = await mailService.mailHR(mailData)
  if (isMailHrSendSuccess(result)) {
    return result
  }
  const failure = {
    template_key: 'reimbursement_attachment',
    to,
    status: result?.status || 'FAILED',
    detail: result,
  }
  console.warn('[reimbursement-email] attachment send failed (non-blocking):', failure)
  const err = new Error('reimbursement email failed')
  err.emailFailure = failure
  throw err
}

/** Resolve stored file_path to a browser-openable URL (S3 presigned or legacy local path). */
export function resolveReimbursementAttachmentUrl(filePath) {
  if (!filePath) return Promise.resolve(null)
  const path = String(filePath).trim().replace(/^\//, '')
  if (!path) return Promise.resolve(null)
  if (/^https?:\/\//i.test(path)) {
    return Promise.resolve(path)
  }
  if (/^(public\/)?uploads\//i.test(path) || path.startsWith('./')) {
    const root = process.env.NODE_ENV === 'production'
      ? 'https://ats.sattvahuman.in/'
      : 'http://192.168.0.30:5000/'
    return Promise.resolve(root + path.replace(/^\.\//, ''))
  }
  const encodedKey = encodeURIComponent(path)
  return axios.get(`${API_URL}reimbursements/file/${encodedKey}`, {
    headers: AuthService.authHeader(),
  }).then(res => {
    const data = res.data
    if (!data || data.status === 'ERROR') return null
    return data.file || data.url || data.data?.file || null
  }).catch(() => null)
}

/**
 * Reimbursement Service — calls `/api/reimbursements` only.
 * Separate from Expense Tracker (`/api/expenses`).
 *
 * Backend contract: see `backend-spec/reimbursements/FRONTEND-API-CONTRACT.md`
 */
class ReimbursementService {

    async resolveAttachmentUrl(filePath) {
      return resolveReimbursementAttachmentUrl(filePath)
    }

    async createReimbursement(data) {
        try {
      const res = await axios.post(`${API_URL}reimbursements`, data, {
        headers: multipartAuthHeaders(),
                })
            return res.data
    } catch (err) {
      return apiError(err, 'Failed to create reimbursement.')
        }
    }

    async getReimbursementById(id) {
        try {
      const res = await axios.get(`${API_URL}reimbursements/${id}`, {
        headers: AuthService.authHeader(),
                })
            return res.data
    } catch (err) {
      return apiError(err)
        }
    }

    async getMyReimbursements(filters = {}) {
        try {
            const res = await axios.get(
        `${API_URL}reimbursements${buildFilterQuery(filters)}`,
                { headers: AuthService.authHeader() },
            )
            return res.data
    } catch (err) {
      return apiError(err, SERVICE_UNAVAILABLE.message)
        }
    }

  /** @deprecated Use getMyReimbursements */
    async getAllReimbursements(filters = {}) {
        return this.getMyReimbursements(filters)
    }

    async getApprovalQueue(filters = {}) {
        try {
            const res = await axios.get(
        `${API_URL}reimbursements/approvals${buildFilterQuery(filters)}`,
                { headers: AuthService.authHeader() },
            )
            return res.data
    } catch (err) {
      return apiError(err, SERVICE_UNAVAILABLE.message)
        }
    }

    async getAllReimbursementsAdmin(filters = {}) {
        try {
            const res = await axios.get(
        `${API_URL}reimbursements/all${buildFilterQuery(filters)}`,
                { headers: AuthService.authHeader() },
            )
            return res.data
    } catch (err) {
      return apiError(err, SERVICE_UNAVAILABLE.message)
        }
    }

    async approveReimbursement(id, payload = {}) {
        try {
            const res = await axios.post(
        `${API_URL}reimbursements/${id}/approve`,
                payload,
                { headers: AuthService.authHeader() },
            )
            return res.data
    } catch (err) {
      if (err?.response?.status === 403) {
        console.warn('[reimbursement] approve 403:', err.response?.data)
      }
      return apiError(err)
        }
    }

    async rejectReimbursement(id, payload = {}) {
        try {
            const res = await axios.post(
        `${API_URL}reimbursements/${id}/reject`,
                payload,
                { headers: AuthService.authHeader() },
            )
            return res.data
    } catch (err) {
      if (err?.response?.status === 403) {
        console.warn('[reimbursement] reject 403:', err.response?.data)
      }
      return apiError(err)
        }
    }

    async updateReimbursement(id, data) {
        try {
      const res = await axios.put(`${API_URL}reimbursements/${id}`, data, {
        headers: AuthService.authHeader(),
      })
            return res.data
    } catch (err) {
      return apiError(err, 'Failed to update reimbursement.')
        }
    }

    /** Multipart PUT when attachment may be included (mirrors expense edit). */
    async editReimbursement(id, data) {
        try {
      const res = await axios.put(`${API_URL}reimbursements/${id}`, data, {
        headers: multipartAuthHeaders(),
      })
            return res.data
    } catch (err) {
      return apiError(err, 'Failed to update reimbursement.')
        }
    }

    async deleteReimbursement(id) {
        try {
      const res = await axios.delete(`${API_URL}reimbursements/${id}`, {
        headers: AuthService.authHeader(),
      })
            return res.data
    } catch (err) {
      return apiError(err, 'Failed to withdraw reimbursement.')
        }
    }

    /** Withdraw own pending claim — alias for DELETE /reimbursements/:id */
    async cancelReimbursement(id) {
        return this.deleteReimbursement(id)
    }

    /**
   * Client-side email trigger (interim until backend sends on create/approve/reject).
   * Set REIMBURSEMENT_EMAIL_ENABLED = false once backend owns mail.
   *
   * Approval-required recipients are NOT resolved on the client. Do not send to
   * hardcoded queue inboxes — backend must email permission-matched approvers only
   * (no fallback L1/L2/Final shared mailboxes).
     */
    async notifyReimbursementEmails(event, row, context = {}) {
    row = row?.data ?? row
    if (!row) return { failures: [] }

      const normalizedRow = normalizeReimbursementEmailRow(row, context.fallback || context.form || {})
      const variables = buildReimbursementEmailVariables(normalizedRow, context)
      const submitterEmail = getSubmitterEmail(normalizedRow)
      const attachmentKey = await ensureReimbursementAttachmentKey({
        ...normalizedRow,
        file_path: context.file_path || normalizedRow.file_path,
      })
      const attachmentPayload = attachmentKey ? { filename: attachmentKey } : {}
      const sends = []
      const fallbackAddedBy = parseAddedBy((context.fallback || context.form || {}).added_by)
      const beneficiaryName = fallbackAddedBy.employee_name || fallbackAddedBy.on_behalf_of || ''
      const beneficiaryId = fallbackAddedBy.employee_id || fallbackAddedBy.applied_for

      if (!REIMBURSEMENT_EMAIL_ENABLED) {
        if (event === 'withdrawn') {
          const remembered = getRememberedReimbursementOnBehalf(normalizedRow.id ?? normalizedRow.claim_id)
          const employeeEmail = remembered?.email
            || fallbackAddedBy.employee_email
            || normalizedRow.employee_email
            || ''
          const to = employeeEmail || submitterEmail
          const cc = (to && submitterEmail && String(to).toLowerCase() !== String(submitterEmail).toLowerCase())
            ? submitterEmail
            : ''
          if (to) {
            sends.push(sendWithdrawnEmailSafe({
              to,
              cc,
              variables: {
                ...variables,
                EmployeeName: remembered?.title || beneficiaryName || variables.EmployeeName,
                EmployeeId: remembered?.empId || beneficiaryId || variables.EmployeeId,
                SubmittedBy: variables.SubmittedBy,
              },
              filename: attachmentKey,
            }))
          }
        }
        const skippedResults = await Promise.allSettled(sends)
        const skippedFailures = skippedResults
          .filter(result => result.status === 'rejected')
          .map(result => result.reason?.emailFailure || result.reason)
        if (skippedFailures.length) {
          console.warn(
            `[reimbursement-email] ${skippedFailures.length} attachment email(s) failed for event "${event}":`,
            skippedFailures,
          )
        }
        return { failures: skippedFailures }
      }

      if (event === 'submitted') {
        if (submitterEmail) {
          sends.push(sendReimbursementEmailSafe({
            template_key: HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_SUBMITTED,
            to: submitterEmail,
            variables: { ...variables, Status: formatReimbursementStatusLabel(REIMBURSEMENT_STATUSES.PENDING_L1) },
            ...attachmentPayload,
          }))
        }
        // L1 approval_required: backend only (permission-based approvers; no client fallback To)
      }

      if (event === 'approved') {
        const prev = context.previousStatus || normalizeReimbursementStatus(row)
        const approverName = formatApproverName(
          row.level1_approved_by || row.level2_approved_by || row.final_approved_by,
        )
        const remark = context.approverComment
          || row.level1_comment || row.level2_comment || row.final_comment || '-'

        if (prev === REIMBURSEMENT_STATUSES.PENDING_L1) {
          if (submitterEmail) {
            sends.push(sendReimbursementEmailSafe({
              template_key: HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_LEVEL1_APPROVED,
              to: submitterEmail,
              variables: {
                ...variables,
                Status: formatReimbursementStatusLabel(row),
                ApprovedBy: formatApproverName(row.level1_approved_by) || approverName,
                Remarks: remark,
                StageApproved: formatReimbursementStageOutcome(REIMBURSEMENT_STATUSES.PENDING_L1, 'approved'),
              },
              ...attachmentPayload,
            }))
          }
          // L2 approval_required: backend only (permission-based; no client fallback To)
        } else if (prev === REIMBURSEMENT_STATUSES.PENDING_L2) {
          if (submitterEmail) {
            sends.push(sendReimbursementEmailSafe({
              template_key: HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_LEVEL2_APPROVED,
              to: submitterEmail,
              variables: {
                ...variables,
                Status: formatReimbursementStatusLabel(row),
                ApprovedBy: formatApproverName(row.level2_approved_by) || approverName,
                Remarks: remark,
                StageApproved: formatReimbursementStageOutcome(REIMBURSEMENT_STATUSES.PENDING_L2, 'approved'),
              },
              ...attachmentPayload,
            }))
          }
          // Final approval_required: backend only (permission-based; no client fallback To)
        } else if (prev === REIMBURSEMENT_STATUSES.PENDING_FINAL || row.status === REIMBURSEMENT_STATUSES.APPROVED) {
          if (submitterEmail) {
            sends.push(sendReimbursementEmailSafe({
              template_key: HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_FINAL_APPROVED,
              to: submitterEmail,
              variables: {
                ...variables,
                Status: formatReimbursementStatusLabel(REIMBURSEMENT_STATUSES.APPROVED),
                ApprovedBy: formatApproverName(row.final_approved_by || row.approved_by) || approverName,
                Remarks: remark,
              },
              ...attachmentPayload,
            }))
          }
        }
      }

      if (event === 'rejected') {
        if (submitterEmail) {
          sends.push(sendReimbursementEmailSafe({
            template_key: HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_REJECTED,
            to: submitterEmail,
            variables: {
              ...variables,
              Stage: context.rejectedStage || row.rejected_stage || '-',
              RejectedBy: formatApproverName(row.rejected_by),
              RejectionComment: context.approverComment || row.rejection_comment || '-',
            },
            ...attachmentPayload,
          }))
        }
      }

    const results = await Promise.allSettled(sends)
    const failures = results
      .filter(result => result.status === 'rejected')
      .map(result => result.reason?.emailFailure || result.reason)

    if (failures.length) {
      console.warn(
        `[reimbursement-email] ${failures.length} notification(s) failed for event "${event}":`,
        failures,
      )
    }

    return { failures }
  }

  /** @deprecated Use notifyReimbursementEmails */
    async sendReimbursementNotification(event, row, context) {
      return this.notifyReimbursementEmails(event, row, context)
    }
}

export default new ReimbursementService()
